import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { US, MX } from 'country-flag-icons/react/3x2';

const LanguageToggle: React.FC = () => {
  const { language, setLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const languages = [
    { code: 'es', name: 'Español', shortName: 'ES', Flag: MX, label: 'MX Español' },
    { code: 'en', name: 'English', shortName: 'EN', Flag: US, label: 'US English' }
  ];

  const currentLanguage = languages.find(l => l.code === language);

  const handleLanguageChange = (langCode: 'es' | 'en') => {
    setLanguage(langCode);
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="text-white hover:text-[#83C441] transition duration-200 flex items-center gap-2"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        {currentLanguage && <currentLanguage.Flag className="w-5 h-5 rounded-sm shadow-sm" aria-hidden="true" />}
        <span className="text-sm">{currentLanguage?.shortName}</span>
        <svg className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute top-full right-0 mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-[60]" role="listbox">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code as 'es' | 'en')}
              className={`w-full text-left px-4 py-3 text-sm hover:bg-green-50 hover:text-[#83C441] transition-colors duration-200 first:rounded-t-md last:rounded-b-md flex items-center gap-3 ${language === lang.code ? 'bg-green-50 text-[#83C441] font-semibold' : 'text-gray-700'
                }`}
              role="option"
              aria-selected={language === lang.code}
            >
              <lang.Flag className="w-6 h-4 rounded-sm shadow-sm" aria-hidden="true" />
              <span>{lang.label}</span>
              {language === lang.code && (
                <svg className="w-4 h-4 ml-auto text-[#83C441]" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageToggle;
